/**
 * Sub-components and link data for the top navigation bar. Keeping these
 * here means ``Navbar.jsx`` is just composition + state, well under the
 * 50-line guideline per component.
 */
import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  UserCircle,
  LogOut,
  Shield,
} from "lucide-react";

export const PUBLIC_LINKS = [
  { label: "How It Works", href: "/#how-it-works", testid: "nav-how-it-works-link" },
  { label: "Benefits", href: "/#benefits", testid: "nav-benefits-link" },
  { label: "Pricing", href: "/#pricing", testid: "nav-pricing-link" },
  { label: "FAQ", href: "/faq", testid: "nav-faq-link" },
  { label: "Contact", href: "/contact", testid: "nav-contact-link" },
];

export const AUTHED_LINKS = [
  { label: "Dashboard", href: "/dashboard", testid: "nav-dashboard-link", icon: LayoutDashboard },
  { label: "Account", href: "/account", testid: "nav-account-link", icon: UserCircle },
];

export const ADMIN_LINK = {
  label: "Admin",
  href: "/admin",
  testid: "nav-admin-link",
  icon: Shield,
};

export function AuthedDesktopLinks({ links }) {
  return (
    <ul
      className="hidden lg:flex items-center gap-8"
      data-testid="nav-authed-links"
    >
      {links.map((link) => (
        <li key={link.href}>
          <Link
            to={link.href}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
            data-testid={link.testid}
          >
            <link.icon className="h-4 w-4" />
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function PublicLinkItem({ link }) {
  const isInternal = link.href.startsWith("/") && !link.href.includes("#");
  return isInternal ? (
    <Link
      to={link.href}
      className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
      data-testid={link.testid}
    >
      {link.label}
    </Link>
  ) : (
    <a
      href={link.href}
      className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
      data-testid={link.testid}
    >
      {link.label}
    </a>
  );
}

export function PublicDesktopLinks({ links }) {
  return (
    <ul
      className="hidden lg:flex items-center gap-8"
      data-testid="nav-public-links"
    >
      {links.map((link) => (
        <li key={link.href}>
          <PublicLinkItem link={link} />
        </li>
      ))}
    </ul>
  );
}

export function DesktopActions({ user, onLogout }) {
  if (user) {
    return (
      <div className="hidden lg:flex items-center gap-3">
        <span
          className="text-sm text-slate-500 max-w-[12rem] truncate"
          data-testid="nav-user-label"
        >
          {user.name || user.email}
        </span>
        <button
          type="button"
          onClick={onLogout}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
          data-testid="nav-logout-button"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </button>
      </div>
    );
  }
  return (
    <div className="hidden lg:flex items-center gap-3">
      <Link
        to="/login"
        className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
        data-testid="nav-login-link"
      >
        Log in
      </Link>
      <Link
        to="/register"
        className="inline-flex items-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-md hover:bg-slate-800 hover:-translate-y-0.5 transition-all"
        data-testid="nav-cta-register-button"
      >
        Get Started
      </Link>
    </div>
  );
}

function MobileLinkItem({ link, isAuthed, onSelect }) {
  const isInternal =
    isAuthed || (link.href?.startsWith("/") && !link.href.includes("#"));
  const cls =
    "block text-base font-medium text-slate-700 hover:text-slate-900";
  return isInternal ? (
    <Link
      to={link.href}
      className={cls}
      onClick={onSelect}
      data-testid={`mobile-${link.testid}`}
    >
      {link.label}
    </Link>
  ) : (
    <a
      href={link.href}
      className={cls}
      onClick={onSelect}
      data-testid={`mobile-${link.testid}`}
    >
      {link.label}
    </a>
  );
}

export function MobileMenu({ open, isAuthed, onClose, onLogout }) {
  if (!open) return null;
  return (
    <div
      className="lg:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur-xl"
      data-testid="nav-mobile-menu"
    >
      <ul className="px-6 py-6 space-y-4">
        {(isAuthed ? AUTHED_LINKS : PUBLIC_LINKS).map((link) => (
          <li key={link.href}>
            <MobileLinkItem
              link={link}
              isAuthed={isAuthed}
              onSelect={onClose}
            />
          </li>
        ))}
        {isAuthed ? (
          <li>
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700"
              data-testid="mobile-nav-logout-button"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </li>
        ) : (
          <>
            <li>
              <Link
                to="/login"
                className="block text-base font-medium text-slate-700 hover:text-slate-900"
                onClick={onClose}
                data-testid="mobile-nav-login-link"
              >
                Log in
              </Link>
            </li>
            <li>
              <Link
                to="/register"
                className="inline-flex items-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-md hover:bg-slate-800 transition-all"
                onClick={onClose}
                data-testid="mobile-nav-cta-register-button"
              >
                Get Started
              </Link>
            </li>
          </>
        )}
      </ul>
    </div>
  );
}
